import { Link, useLocation, useNavigate } from 'react-router-dom';
import BrandLogo from './BrandLogo';

interface SidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export default function Sidebar({ collapsed = false, onToggleCollapse }: SidebarProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const userStr = localStorage.getItem('user');
  let user: { role?: string } | null = null;
  try {
    user = userStr ? JSON.parse(userStr) : null;
  } catch { user = null; }
  const role = String(user?.role || '').toLowerCase();
  const isStaff = ['admin', 'owner', 'dueno', 'dueño'].includes(role);

  const handleLogout = () => {
    localStorage.removeItem('jwt');
    localStorage.removeItem('user');
    navigate('/login');
    window.location.reload();
  };

  const ordersLabel = isStaff ? 'Ventas' : 'Mis compras';
  const navAll = [
    { to: '/dashboard', label: 'Dashboard', icon: <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M13 5v6h6m-6 0v6m0 0H7m6 0h6" /></svg> },
    { to: '/licenses', label: isStaff ? 'Licencias' : 'Mis licencias', icon: <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg> },
    { to: '/orders', label: ordersLabel, icon: <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg> },
    { to: '/encrypt-lua', label: 'Encriptar producto', icon: <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0-1.657 1.343-3 3-3h1V6a4 4 0 10-8 0v2h1c1.657 0 3 1.343 3 3zm-7 0h14v9H5v-9z" /></svg> },
    { to: '/products', label: 'Productos', icon: <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg> },
    { to: '/clients', label: 'Clientes', icon: <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg> },
    { to: '/users', label: 'Usuarios', icon: <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg> },
    { to: '/cart', label: 'Carrito', icon: <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13h10l-2 8H7L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></svg> },
  ];

  const nav = isStaff
    ? navAll
    : navAll.filter(i => ['/dashboard', '/licenses', '/orders', '/cart'].includes(i.to));

  return (
    <aside
      className={`
        min-h-screen bg-rs-surface border-r border-rs-border flex flex-col
        transition-all duration-300 ease-in-out
        ${collapsed ? 'w-[72px]' : 'w-64'}
      `}
    >
      <div className="py-6 px-4 flex flex-col h-full">
        <div className={`flex mb-8 ${collapsed ? 'flex-col items-center gap-3' : 'items-center justify-between'}`}>
          <Link to="/" className={`flex items-center gap-2 px-1 overflow-hidden ${collapsed ? 'justify-center' : ''}`}>
            {collapsed ? (
              <BrandLogo size="sm" showWordmark={false} />
            ) : (
              <span className="flex flex-col">
                <BrandLogo size="sm" alwaysShowWordmark />
                <span className="text-[10px] uppercase tracking-widest text-gray-500 mt-1 px-1">
                  {isStaff ? 'Admin' : 'Mi cuenta'}
                </span>
              </span>
            )}
          </Link>
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="p-2 rounded-lg text-gray-400 hover:bg-rs-surfaceLight hover:text-gray-200 transition-colors flex-shrink-0"
              title={collapsed ? 'Expandir menú' : 'Recoger menú'}
            >
              {collapsed ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              )}
            </button>
          )}
        </div>

        <nav className="flex-1 space-y-1">
          {nav.map(item => {
            const isActive = pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                title={collapsed ? item.label : undefined}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all
                  ${collapsed ? 'justify-center px-3' : ''}
                  ${isActive
                    ? 'bg-rs-accent/20 text-rs-accent border border-rs-accent/40'
                    : 'text-gray-400 hover:bg-rs-surfaceLight hover:text-gray-200'
                  }
                `}
              >
                {item.icon}
                {!collapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <button
          onClick={handleLogout}
          className={`
            flex items-center gap-3 px-4 py-3 rounded-lg font-medium
            text-gray-400 hover:bg-red-500/20 hover:text-red-400
            transition-all mt-4 border border-transparent hover:border-red-500/30
            ${collapsed ? 'justify-center px-3' : ''}
          `}
          title={collapsed ? 'Cerrar sesión' : undefined}
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          {!collapsed && <span>Cerrar sesión</span>}
        </button>
      </div>
    </aside>
  );
}
